// Types
import type { ClassicalMenuItem } from '../../api/types/context-menu';
import type { ContextMenuItem } from './types';

// Actions
import { makeContextualMenuPerformAction } from '@jet-app/music-classical/action-model/contextual-menu/PerformAction';
import { makeContextualMenuDeleteFromLibraryAction } from '@jet-app/music-classical/action-model/contextual-menu/DeleteFromLibraryAction';
import { playActionForClassicalPlayAction } from '@jet-app/music-classical/action-model/PlayAction';
import { createFlowActionForAction } from '@jet-app/music-classical/utilities/create-flow-action';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { makeCompoundAction } from '@jet-app/music-classical/action-model';
import { makeCloseFullScreenPlayerAction } from '@jet-app/music-classical/action-model/CloseFullScreenPlayerAction';

export interface SectionWithMenuItems {
    menuItems: ClassicalMenuItem[];
}

export function transformToContextMenuItems(
    sections: SectionWithMenuItems[] | undefined,
    objectGraph: ObjectGraph,
): ContextMenuItem[] {
    return (
        sections?.reduce(
            (menuItems: ContextMenuItem[], section: SectionWithMenuItems) => {
                const newItems = section.menuItems.map((item) => ({
                    icon: item?.icon,
                    label: item?.title,
                    action: getActionForItem(item, objectGraph),
                }));
                return menuItems.concat(newItems);
            },
            [],
        ) ?? []
    );
}

export function transformUserComponentsToContextMenuItems(
    buttons: ClassicalMenuItem[],
    objectGraph: ObjectGraph,
    additionalActionPayload: { [key: string]: string } = {},
): ContextMenuItem[] {
    return buttons?.map((item) => ({
        icon: item?.icon,
        label: item?.title,
        action: getActionForItem(item, objectGraph, additionalActionPayload),
    }));
}

function getActionForItem(
    item: ClassicalMenuItem,
    objectGraph: ObjectGraph,
    additionalActionPayload: { [key: string]: string },
) {
    switch (item.action?.type) {
        case 'confirm-delete':
            return makeContextualMenuDeleteFromLibraryAction({
                title: item.title,
                ...item.action,
                deleteButton: {
                    title: item?.action?.deleteButton?.title,
                    action: makeContextualMenuPerformAction(
                        item?.action.deleteButton?.action?.type,
                        item?.action.deleteButton?.action?.url,
                        item?.action.deleteButton?.action?.payload,
                    ),
                },
            });

        case 'play-context':
            return playActionForClassicalPlayAction(item.action);

        case 'componentScreen':
            const screenAction = createFlowActionForAction(
                item.action,
                objectGraph,
            );

            if (item.action.screenType === 'album') {
                const actionMetrics = { data: [], custom: {} };
                const subactions = [
                    screenAction,
                    makeCloseFullScreenPlayerAction(),
                ];

                return makeCompoundAction(actionMetrics, subactions);
            }
            return screenAction;

        case 'edit-playlist':
            item.action.payload = {
                ...item.action.payload,
                ...additionalActionPayload,
            };
        default:
            // TODO: Ask Classical Server to Provide a unique Action for This
            // rdar://140428971 ([Web] Ctx Menu Action for Add to Playlist should contain the URL to add those items to a Playlist and not to fetch Playlists as Web does not have a list screen but a ctx menu - sub menu)
            const actionType =
                item?.icon === 'addToPlaylist'
                    ? 'addToPlaylist'
                    : item?.action.type;

            return makeContextualMenuPerformAction(
                actionType,
                item?.action.url,
                item?.action.payload,
            );
    }
}
